import { useState, useEffect } from 'react';
import { createLogger } from '@/lib/logger';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, Alert } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { StatusBar } from 'expo-status-bar';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/lib/supabase';
import { useRouter } from 'expo-router';
import { FLAGS } from '@/lib/featureFlags';

const log = createLogger('INDEX');

export default function ChallengesList() {
  const { profile } = useAuth();
  const { theme } = useTheme();
  const router = useRouter();
  const [loading, setLoading] = useState(true);
  const [challenges, setChallenges] = useState<any[]>([]);
  const [optedIn, setOptedIn] = useState<Record<string, boolean>>({});
  const [gymMap, setGymMap] = useState<Record<string,string>>({});

  // V1: Challenges disabled — redirect to home
  useEffect(() => {
    if (!FLAGS.V1_CHALLENGES_ENABLED) {
      router.replace('/(tabs)/home');
    }
  }, [router]);

  useEffect(() => {
    load();
  }, [profile?.gym_id]);

  const load = async () => {
    if (!profile?.gym_id) {
      setChallenges([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('challenges')
        .select('*')
        .eq('scope_type', 'gym')
        .eq('scope_id', profile.gym_id)
        .gte('ends_at', now)
        .order('ends_at', { ascending: true });

      if (error) throw error;

      const list = (data as any[]) || [];
      setChallenges(list);

      // Cache gym names for any challenges issued by a gym
      try {
        const gymIds = Array.from(new Set(list.filter(c => c.issuer_type === 'gym' && c.issuer_gym_id).map(c => c.issuer_gym_id)));
        if (gymIds.length > 0) {
          const { data: gyms } = await supabase.from('gyms').select('id,name').in('id', gymIds as any[]);
          const map: Record<string,string> = {};
          (gyms || []).forEach((g: any) => { if (g?.id) map[g.id] = g.name; });
          setGymMap(map);
        } else {
          setGymMap({});
        }
      } catch (e) {
        log.error('Failed to load gyms for challenges list', e);
        setGymMap({});
      }

      const ids = list.map((c) => c.id).filter(Boolean);
      if (ids.length > 0) {
        const { data: parts } = await supabase
          .from('challenge_participants')
          .select('challenge_id')
          .eq('profile_id', profile.id)
          .in('challenge_id', ids);

        const set: Record<string, boolean> = {};
        (parts || []).forEach((p: any) => (set[p.challenge_id] = true));
        setOptedIn(set);
      } else {
        setOptedIn({});
      }
    } catch (err: any) {
      log.error('Failed to load challenges', err);
      Alert.alert('Oops', "Couldn't load challenges. Pull down to retry.");
    } finally {
      setLoading(false);
    }
  };

  const handleOptIn = async (challengeId: string) => {
    if (!profile) return;
    try {
      const now = new Date().toISOString();
      const payload = { challenge_id: challengeId, profile_id: profile.id, status: 'opted_in', opted_in_at: now };
      const { error } = await supabase.from('challenge_participants').upsert([payload], { onConflict: 'challenge_id,profile_id' });
      if (error) throw error;
      setOptedIn((s) => ({ ...s, [challengeId]: true }));
    } catch (err: any) {
      log.error('Opt in failed', err);
      Alert.alert('Oops', "Couldn't join this challenge. Please try again.");
    }
  };

  if (loading) {
    return (
      <View style={[styles.center, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.accent} />
      </View>
    );
  }

  if (challenges.length === 0) {
    return (
      <View style={[styles.center, { backgroundColor: theme.colors.background }]}>
        <Text style={[styles.emptyText, { color: theme.colors.text }]}>No active challenges.</Text>
      </View>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: theme.colors.background }} edges={['top']}>
      <StatusBar style="light" />
      <View style={[styles.headerRow, { backgroundColor: theme.colors.background }]}>
        <View style={{ width: 48 }} />
        <View style={styles.headerTitleWrap}>
          <Text style={[styles.headerTitleText, { color: theme.colors.text }]}>Challenges</Text>
        </View>
        {((profile && profile.is_admin) ? (
          <TouchableOpacity onPress={() => router.push('/(profile)/challenges/create')} style={styles.headerButton}>
            <Text style={[styles.headerButtonText, { color: theme.colors.accent }]}>Create</Text>
          </TouchableOpacity>
        ) : (
          <View style={{ width: 48 }} />
        ))}
      </View>

      <ScrollView style={[styles.container, { backgroundColor: theme.colors.background }]} contentContainerStyle={{ paddingTop: 12, paddingBottom: 28 }}> 
        {challenges.map((c) => (
          <View key={c.id} style={[styles.card, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}> 
            <Text style={[styles.title, { color: theme.colors.text }]} numberOfLines={1}>{c.title || 'Untitled'}</Text>
            <Text style={[styles.meta, { color: theme.colors.textSecondary }]}>{new Date(c.starts_at).toLocaleDateString()} — {new Date(c.ends_at).toLocaleDateString()}</Text>
            {(() => {
              const issuerLabel = c.issuer_type === 'admin'
                ? 'DUEL'
                : (c.issuer_type === 'gym' ? (c.issuer_gym_id ? (gymMap[c.issuer_gym_id] || 'Gym') : 'Gym') : 'DUEL');
              return <Text style={[styles.meta, { color: theme.colors.textSecondary }]}>{`Issued by ${issuerLabel}`}</Text>;
            })()}
            <View style={styles.row}>
              {optedIn[c.id] ? (
                <TouchableOpacity style={[styles.optedInButton, { backgroundColor: theme.colors.surfaceVariant }]} disabled>
                  <Text style={{ color: theme.colors.textSecondary }}>Opted In</Text>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity style={[styles.optInButton, { backgroundColor: theme.colors.accent }]} onPress={() => handleOptIn(c.id)}>
                  <Text style={{ color: theme.colors.accentForeground }}>Opt In</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity style={[styles.detailsButton, { borderColor: theme.colors.border }]} onPress={() => router.push(`/challenges/${c.id}`)}>
                <Text style={{ color: theme.colors.textSecondary }}>Details</Text>
              </TouchableOpacity>
            </View>
          </View>
        ))}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: 16 },
  headerRow: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 8, minHeight: 44 },
  headerButton: { padding: 8, minWidth: 48, minHeight: 44, justifyContent: 'center' },
  headerButtonText: { fontSize: 15, fontWeight: '600' },
  headerTitleWrap: { flex: 1, alignItems: 'center' },
  headerTitleText: { fontSize: 15, fontWeight: '700' },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  emptyText: { fontSize: 16, fontWeight: '700' },
  card: { borderWidth: 1, borderRadius: 2, padding: 12, marginBottom: 12 },
  title: { fontSize: 16, fontWeight: '800' },
  meta: { fontSize: 13, marginTop: 6 },
  row: { flexDirection: 'row', gap: 12, marginTop: 12 },
  optInButton: { paddingVertical: 10, paddingHorizontal: 16, borderRadius: 2 },
  optedInButton: { paddingVertical: 10, paddingHorizontal: 16, borderRadius: 2 },
  detailsButton: { paddingVertical: 10, paddingHorizontal: 12, borderRadius: 2, borderWidth: 1 },
});
